import { Routes } from "@angular/router";
import { blockIdGuard } from '@share/guards/block-id.guard';
import { eventIdGuard } from '@share/guards/event-id.guard';
import { strategicAreaIdGuard } from '@share/guards/strategic-area-id.guard';
import { blockResolver } from "@share/resolvers/block.resolver";
import { pendingChangesGuard } from "@share/guards/pending-changes.guard";
import { CalGuardService, RoleGuardService } from "@cvx/cal-angular";
import { eventResolver } from "./share/resolvers/event.resolver";


export const routes: Routes = [
  {
    path: '', pathMatch: 'full', redirectTo: 'tracking/blocks'    
  },
  {
    path: 'tracking',
    loadComponent: () => import("./features/tracking/tracking").then(m => m.Tracking),
    canActivate: [CalGuardService],
    children: [
      {
        path: '', pathMatch: 'full', redirectTo: 'blocks'
      },
      {
        path: 'blocks',
        loadComponent: () => import("./features/tracking/components/block-summary/block-summary").then(m => m.Summary),
        data: {
          breadcrumbs: [
            { label: 'blocks', route: null },
          ]
        },
      },
      {
        path: 'blocks/add',
        loadComponent: () => import("./features/tracking/components/block-editing/add-block/add-block").then(m => m.AddBlock),
        canDeactivate: [pendingChangesGuard],
        data: {
          breadcrumbs: [
            { label: 'blocks', route: 'blocks' },
            { label: 'add', route: null },
          ]
        },
      },
      {
        path: 'blocks/:blockId',
        loadComponent: () => import("./features/tracking/components/block-details/block-details").then(m => m.BlockDetails),
        canActivate: [blockIdGuard],
        resolve: { block: blockResolver },
        data: {
          breadcrumbs: [
            { label: 'blocks', route: 'blocks' },
            { label: ':blockName', route: null },
          ]
        },
      },
      {
        path: 'blocks/:blockId/edit',
        loadComponent: () => import("./features/tracking/components/block-editing/edit-block/edit-block").then(m => m.EditBlock),
        canActivate: [blockIdGuard],
        canDeactivate: [pendingChangesGuard],
        resolve: { block: blockResolver },
        data: {
          breadcrumbs: [
            { label: 'blocks', route: 'blocks' },
            { label: ':blockName', route: 'blocks/:blockId' },
            { label: 'edit', route: null },
          ]
        },
      },
      //{
      //  path: 'blocks/:blockId/events',
      //  loadComponent: () => import("./features/tracking/components/event-summary/event-summary").then(m => m.EventSummary),
      //  canActivate: [blockIdGuard],
      //  resolve: { block: blockResolver },
      //  data: {
      //    breadcrumbs: [
      //      { label: 'blocks', route: 'blocks' },
      //      { label: ':blockName', route: 'blocks/:blockId' },
      //      { label: 'events', route: null },
      //    ]
      //  },
      //},
      {
        path: 'blocks/:blockId/events/add',
        loadComponent: () => import("./features/tracking/components/event-creation/event-creation").then(m => m.EventCreation),
        canActivate: [blockIdGuard],
        canDeactivate: [pendingChangesGuard],
        resolve: { block: blockResolver },
        data: {
          breadcrumbs: [
            { label: 'blocks', route: 'blocks' },
            { label: ':blockName', route: 'blocks/:blockId' },
            { label: 'events', route: null },
            { label: 'add', route: null },
          ]
        },
      },
      {
        path: 'blocks/:blockId/events/:eventId',
        loadComponent: () => import("./features/tracking/components/event-details/event-details").then(m => m.EventDetails),
        canActivate: [blockIdGuard, eventIdGuard],
        resolve: { block: blockResolver },
        data: {
          breadcrumbs: [
            { label: 'blocks', route: 'blocks' },
            { label: ':blockName', route: 'blocks/:blockId' },
            { label: 'events', route: null },
            { label: ':eventId', route: null },
          ]
        },
      },
      {
        path: 'blocks/:blockId/events/:eventId/edit',
        loadComponent: () => import("./features/tracking/components/event-editing/event-editing").then(m => m.EventEditing),
        canActivate: [blockIdGuard, eventIdGuard],
        canDeactivate: [pendingChangesGuard],
        resolve: { blockEvent: eventResolver, block: blockResolver },
        data: {
          breadcrumbs: [
            { label: 'blocks', route: 'blocks' },
            { label: ':blockName', route: 'blocks/:blockId' },
            { label: 'events', route: null },
            { label: ':eventId', route: 'blocks/:blockId/events/:eventId' },
            { label: 'edit', route: null },
          ]
        },
      }      
    ]
  },
  {
    path: 'reports',
    loadComponent: () => import("./features/reports/reports").then(m => m.Reports),
    children: [
      {
        path: '', pathMatch: 'full', redirectTo: 'waterfall'
      },
      {
        path: 'waterfall',
        loadComponent: () => import("./features/reports/components/waterfall-report/waterfall-report").then(m => m.WaterfallReport)
      },
      {
        path: 'year-to-date-waterfall',
        loadComponent: () => import("./features/reports/components/year-to-date-waterfall-report/year-to-date-waterfall-report").then(m => m.YearToDateWaterfallReport)
      },
      {
        path: 'year-on-year',
        loadComponent: () => import("./features/reports/components/year-over-year-report/year-over-year-report").then(m => m.YearOverYearReport)
      }
    ]
  },
  {
    path: 'about',
    loadComponent: () => import("./features/home/home").then(m => m.Home)
  },
  {
    path: 'admin',
    loadComponent: () => import("./features/admin/admin").then(m => m.Admin),
    canActivate: [CalGuardService, RoleGuardService],
    data: {
      roles: ['Acreage.Admin', 'Acreage.Developer']
    },
    children: [
      {
        path: '', pathMatch: 'full', redirectTo: 'authorization'
      },      
      {
        path: 'authorization',
        loadComponent: () => import("./features/admin/components/strategic-areas/strategic-areas").then(m => m.StrategicAreas)
      },
      {
        path: 'authorization/add',
        loadComponent: () => import("./features/admin/components/strategic-area-create/strategic-area-create").then(m => m.StrategicAreaCreate)
      },
      {
        path: 'authorization/import',
        loadComponent: () => import("./features/admin/components/acreage-import/acreage-import").then(m => m.AcreageImport)
      },
      {
        path: 'authorization/:id',
        loadComponent: () => import("./features/admin/components/strategic-area-edit/strategic-area-edit").then(m => m.StrategicAreaEdit),
        canActivate: [strategicAreaIdGuard]
      }
    ]
  }
];
